/* ───────────────────────────────────────────────────────────
   DHE Bundle PDP — Editorial flash drop
   Countdown · viewer count · sticky CTA · inventory poll
   ─────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var URGENT_THRESHOLD_MS = 3600000; // 1 hour

  /* ── Time helpers ── */
  function getTodayExpiryMs() {
    var now = new Date();
    // Parse NY time components via regex — avoids new Date(localeStr) which is
    // unreliable on Safari iOS and would return NaN, falsely triggering "Drop ended".
    var nyStr = now.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: '2-digit', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    var parts = nyStr.match(/(\d+)\/(\d+)\/(\d+),?\s*(\d+):(\d+):(\d+)/);
    if (!parts) return null;
    var month = +parts[1] - 1, day = +parts[2], year = +parts[3];
    var hour = +parts[4], min = +parts[5], sec = +parts[6];
    if (hour >= 21) return null; // Past 9pm ET
    // Compute UTC offset from the parsed NY local time
    var nyLocal = new Date(year, month, day, hour, min, sec);
    var offsetMs = now.getTime() - nyLocal.getTime();
    var nyExpiry = new Date(year, month, day, 21, 0, 0);
    return nyExpiry.getTime() + offsetMs;
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  /* ── Countdown ── */
  function initCountdown(section) {
    var hEl = section.querySelector('[data-clock-h]');
    var mEl = section.querySelector('[data-clock-m]');
    var sEl = section.querySelector('[data-clock-s]');
    var mirrorEl = section.querySelector('[data-bundle-clock-mirror]');
    var topbarEl = section.querySelector('[data-bundle-topbar-clock]');
    var dropClockEl = section.querySelector('[data-bundle-drop-clock]');
    var expiredEl = section.querySelector('[data-bundle-clock-expired]');
    var clockEl = section.querySelector('[data-bundle-clock]');
    if (!hEl || !mEl || !sEl) return;

    var expiryMs = getTodayExpiryMs();
    if (!expiryMs) {
      hEl.textContent = mEl.textContent = sEl.textContent = '00';
      if (clockEl) clockEl.style.display = 'none';
      if (expiredEl) expiredEl.removeAttribute('hidden');
      if (topbarEl) topbarEl.textContent = 'Drop ended';
      return;
    }

    var lastSecond = -1;
    function tick() {
      var rem = expiryMs - Date.now();
      if (rem <= 0) {
        hEl.textContent = mEl.textContent = sEl.textContent = '00';
        if (clockEl) clockEl.style.display = 'none';
        if (expiredEl) expiredEl.removeAttribute('hidden');
        if (dropClockEl) dropClockEl.textContent = 'Ended';
        if (topbarEl) topbarEl.textContent = 'Drop ended';
        section.dataset.bundleExpired = 'true';
        handleExpiry(section);
        return;
      }

      var totalSec = Math.floor(rem / 1000);
      if (totalSec === lastSecond) {
        requestAnimationFrame(tick);
        return;
      }
      lastSecond = totalSec;

      var h = Math.floor(totalSec / 3600);
      var m = Math.floor((totalSec % 3600) / 60);
      var s = totalSec % 60;

      hEl.textContent = pad(h);
      mEl.textContent = pad(m);
      sEl.textContent = pad(s);

      if (mirrorEl) {
        mirrorEl.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      }

      if (topbarEl) {
        topbarEl.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      }

      if (dropClockEl) {
        dropClockEl.textContent = pad(h) + ':' + pad(m) + ':' + pad(s);
      }

      // Urgency state — last hour
      if (rem < URGENT_THRESHOLD_MS && section.dataset.urgent !== 'true') {
        section.dataset.urgent = 'true';
      }

      requestAnimationFrame(tick);
    }
    tick();
  }

  /* ── Viewer count ── */
  function seededRand(seed, min, max) {
    var s = Math.abs((seed * 2654435761) & 0x7fffffff);
    return (s % (max - min)) + min;
  }

  function initViewers(section) {
    var el = section.querySelector('[data-bundle-viewers]');
    if (!el) return;
    var productId = parseInt(section.dataset.productId || '0', 10);
    var expiryMs = getTodayExpiryMs();
    var inSurge = expiryMs && (expiryMs - Date.now()) < URGENT_THRESHOLD_MS;
    var current = seededRand(productId, inSurge ? 8 : 4, inSurge ? 22 : 18);

    function update() {
      var countEl = el.querySelector('[data-bundle-viewer-count]');
      if (countEl) countEl.textContent = current;
      var topbarCount = section.querySelector('[data-bundle-topbar-viewers]');
      if (topbarCount) topbarCount.textContent = current;
      el.setAttribute('aria-label', current + ' stylists viewing');
    }

    var nextDrift = Date.now() + seededRand(productId ^ 0xdead, 8000, 12000);
    function drift() {
      if (Date.now() >= nextDrift) {
        var inSurgeNow = expiryMs && (expiryMs - Date.now()) < URGENT_THRESHOLD_MS;
        var delta = Math.random() < 0.5 ? 1 : -1;
        var lo = inSurgeNow ? 8 : 4;
        var hi = inSurgeNow ? 22 : 18;
        current = Math.min(hi, Math.max(lo, current + delta));
        update();
        nextDrift = Date.now() + seededRand(Date.now() & 0xffff, 8000, 12000);
      }
      setTimeout(drift, 1000);
    }
    update();
    setTimeout(drift, Math.max(0, nextDrift - Date.now()));
  }

  /* ── Sold-out / expiry ── */
  function markSoldOut(section, label) {
    section.dataset.soldOut = 'true';
    var atc = section.querySelector('[data-bundle-atc]');
    var stickyAtc = section.querySelector('[data-bundle-sticky-cta]');
    var overlay = section.querySelector('[data-bundle-sold-overlay]');
    if (atc) {
      atc.disabled = true;
      var labelEl = atc.querySelector('.dh-bdrop__cta-label');
      if (labelEl) labelEl.textContent = label || 'Sold Out';
      else atc.textContent = label || 'Sold Out';
      atc.classList.add('dh-bdrop__cta--sold');
    }
    if (stickyAtc) {
      stickyAtc.disabled = true;
      stickyAtc.textContent = label || 'Sold';
    }
    if (overlay) overlay.removeAttribute('hidden');
  }

  function handleExpiry(section) { markSoldOut(section, 'This Drop Has Ended'); }

  /* ── Inventory poll ── */
  function pollInventory(section) {
    var handle = section.dataset.productHandle;
    if (!handle) return;
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var qty = data.variants && data.variants[0] ? data.variants[0].inventory_quantity : null;
        if (qty !== null && qty <= 0) markSoldOut(section, 'Sold');
      })
      .catch(function () {});
  }

  function initInventoryPoller(section) {
    pollInventory(section);
    var iv = setInterval(function () {
      if (section.dataset.bundleExpired) { clearInterval(iv); return; }
      pollInventory(section);
    }, 60000);
  }

  /* ── Recent drops archive ── */
  function loadArchive(section) {
    var archive = section.querySelector('[data-bundle-archive]');
    var grid = section.querySelector('[data-bundle-archive-grid]');
    var emptyEl = section.querySelector('[data-bundle-archive-empty]');
    if (!archive || !grid) return;

    var currentHandle = section.dataset.productHandle;

    function showEmpty() {
      if (emptyEl) emptyEl.removeAttribute('hidden');
      archive.removeAttribute('hidden');
    }

    function fmt(cents) {
      var n = parseFloat(cents) || 0;
      return '$' + Math.round(n).toLocaleString('en-US');
    }

    fetch('/collections/daily-drop-bank/products.json?limit=50&sort_by=created-descending')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        var past = (data.products || [])
          .filter(function (p) {
            return p.handle !== currentHandle &&
                   p.product_type === 'Daily Drop Bundle' &&
                   p.variants && p.variants[0];
          })
          .slice(0, 8);

        if (past.length === 0) { showEmpty(); return; }

        past.forEach(function (p) {
          var card = document.createElement('a');
          card.className = 'dh-bdrop__archive-card';
          card.href = '/products/' + p.handle;

          var media = document.createElement('div');
          media.className = 'dh-bdrop__archive-card-media';
          if (p.images && p.images[0]) {
            var img = document.createElement('img');
            img.src = p.images[0].src || p.images[0];
            img.alt = p.title;
            img.loading = 'lazy';
            img.width = 200;
            img.height = 250;
            media.appendChild(img);
          }
          var v0 = p.variants && p.variants[0] ? p.variants[0] : {};
          var soldOut = v0.available === false || v0.inventory_quantity === 0;

          var stamp = document.createElement('span');
          stamp.className = 'dh-bdrop__archive-card-stamp';
          stamp.textContent = soldOut ? 'Sold' : 'Retired';
          media.appendChild(stamp);
          card.appendChild(media);

          var title = document.createElement('div');
          title.className = 'dh-bdrop__archive-card-title';
          title.textContent = p.title;
          card.appendChild(title);

          var price = document.createElement('div');
          price.className = 'dh-bdrop__archive-card-price';
          var rawPrice = v0.price || 0;
          price.textContent = (soldOut ? 'Sold for ' : 'Was ') + fmt(rawPrice);
          card.appendChild(price);

          grid.appendChild(card);
        });

        archive.removeAttribute('hidden');
      })
      .catch(function () { showEmpty(); });
  }


  /* ── Hide floating chat widgets — they compete with the drop CTA ── */
  function hideChatWidgets() {
    var selectors = [
      '#dh-drop-chat', '.dh-chat', '.dh-colorring',
      '[id*="colorring"]', '[id*="color-match"]',
      'shopify-chat', '#shopify-chat'
    ];
    selectors.forEach(function (sel) {
      try {
        document.querySelectorAll(sel).forEach(function (el) {
          el.style.setProperty('display', 'none', 'important');
        });
      } catch (e) {}
    });
    // Also catch anything with "ColorRing" or "color-match" in the class name
    document.querySelectorAll('*').forEach(function (el) {
      var cls = (el.className && typeof el.className === 'string') ? el.className : '';
      var id = el.id || '';
      if (/ColorRing|color.?match/i.test(cls + id)) {
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  /* ── Image gallery ── */
  function initGallery(section) {
    var mainImg = section.querySelector('.dh-bdrop__media > img');
    if (!mainImg) return;
    var thumbs = section.querySelectorAll('[data-gallery-btn]');
    if (!thumbs.length) return;
    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        mainImg.src = btn.dataset.fullSrc;
        mainImg.srcset = btn.dataset.fullSrcset || '';
        thumbs.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
      });
    });
  }

  /* ── Description expand/collapse ── */
  function initLedeToggle(section) {
    var lede = section.querySelector('[data-lede]');
    var btn  = section.querySelector('[data-lede-toggle]');
    if (!lede || !btn) return;
    btn.addEventListener('click', function () {
      var expanded = lede.classList.toggle('is-expanded');
      btn.textContent = expanded ? 'Read less' : 'Read more';
      btn.setAttribute('aria-expanded', expanded);
    });
  }

  /* ── Init ── */
  function init() {
    var section = document.querySelector('[data-bundle-pdp]');
    if (!section) return;
    hideChatWidgets();
    initLedeToggle(section);
    initGallery(section);
    // Also observe for late-injected widgets (app scripts load after DOM)
    if (window.MutationObserver) {
      var obs = new MutationObserver(function (mutations) {
        var affected = mutations.some(function (m) { return m.addedNodes.length; });
        if (affected) hideChatWidgets();
      });
      obs.observe(document.body, { childList: true, subtree: true });
      // Disconnect after 8 seconds — widgets are loaded by then
      setTimeout(function () { obs.disconnect(); }, 8000);
    }
    initCountdown(section);
    initViewers(section);
    initInventoryPoller(section);
    loadArchive(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());

